import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, Trash, Upload, Crop as CropIcon } from "lucide-react";
import { blobToDataURL, getCroppedImg } from "../../utils/cropImage";

const LogoUploaderModal = ({ isOpen, onClose, onSave }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError("La imagen no debe superar los 2MB.");
      return;
    }

    setError(""); // Limpiar errores si todo está bien

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setPreview(null);
    };
    reader.readAsDataURL(file);
  };

  const handleCrop = async () => {
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(blob);
      setPreview(previewUrl);
    } catch (error) {
      console.error("Error al recortar imagen", error);
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    try {
      const blob = await fetch(preview).then(res => res.blob());
      // Si necesitas el Data URL (opcional)
      const dataUrl = await blobToDataURL(blob);
      onSave(blob, dataUrl);
      onClose();
    } catch (error) {
      console.error("Error al guardar imagen", error);
    }
  };

  const handleRemoveImage = () => {
    setImageSrc(null);
    setPreview(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setError(""); // Limpiar errores
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-800"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-semibold mb-4">Cambiar Logo</h2>

        {!imageSrc ? (
          <div className="border-2 border-dashed border-gray-300 p-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              Sube una imagen cuadrada (proporción 1:1)
            </p>
            <p className="text-sm text-gray-600 mb-2">
              La imagen no debe superar los 2MB y debe ser en formato PNG o JPG.
            </p>
            <label className="block w-fit mx-auto cursor-pointer bg-[#0488AF] hover:bg-[#03779C] text-white font-medium py-2 px-4 rounded-lg shadow transition">
              Subir imagen
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {error && (
              <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
            )}
          </div>
        ) : (
          <>
            <div className="md:flex gap-6">
              {/* Cropper */}
              <div className="flex-1">
                <div className="relative w-full h-[300px] bg-gray-100 rounded overflow-hidden mb-4">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">Tamaño</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(+e.target.value)}
                    className="w-full mx-2"
                  />
                  <span className="text-sm text-gray-500">Zoom</span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleRemoveImage}
                    className="flex items-center gap-1 px-3 py-2 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200"
                  >
                    <Trash size={16} /> Quitar imagen
                  </button>
                  <label className="flex items-center gap-1 px-3 py-2 text-sm rounded bg-[#0488AF] text-white hover:bg-[#03779C] cursor-pointer">
                    <Upload size={16} />
                    Subir otra
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
                )}
              </div>

              {/* Preview */}
              <div className="flex flex-col items-center justify-between mt-6 md:mt-0">
                <div className="text-center mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Vista previa:</h4>
                  <div className="w-48 h-48 rounded-full overflow-hidden border shadow">
                    <img
                      src={preview || imageSrc}
                      alt="Vista previa"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleCrop}
                    className="flex items-center gap-1 px-4 py-2 text-sm rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <CropIcon size={16} /> Recortar
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={!preview}
                    className={`px-4 py-2 text-sm rounded ${
                      preview
                        ? "bg-[#1BBB55] text-white hover:bg-[#1aab51]"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LogoUploaderModal;
